import { push } from 'connected-react-router';
import { put, select, takeLatest } from 'redux-saga/effects';

import { checkToken, registerUserWithDetails, resetPasswordNonAuth } from '../../api/auth';
import { login, logout, requestResetPassword } from '../../api/user';
import { NOTIFICATION_TYPE_INTERACTION } from '../../attrs/notifications';
import { generateId } from '../../helpers/utils';
import { addNotification } from '../ui/notifications/actions';
import { setUserInformation } from '../user/actions';
import { actionStatus, getError, getMessage, getUserInformationFromToken, statusAction } from '../utils';
import { checkAuthentication, setAuthentication, setLoginSession, updateAuthTokens } from './actions';
import * as constants from './constants';
import { getIsAuthenticated } from './selectors';
import { LOGIN_SESSION_STORAGE_KEY, restoreLoginSession, startLoginSession } from '../../api/loginSession';

function* handleUpdateTokens({ data }) {
  if (data) {
    yield localStorage.setItem('accessToken', data.access);
    yield localStorage.setItem('refreshToken', data.refresh);
    yield put(checkAuthentication());
  } else {
    yield localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
    yield localStorage.removeItem('refreshToken');
    yield localStorage.removeItem('accessToken');
    yield put(setAuthentication(false));

    if (window.location.pathname !== '/login') {
      yield put(push('/login'));
    }
  }
}

function* handleLogin({ userLogin, password, recaptchaValue }) {
  yield put(statusAction(constants.LOGIN, actionStatus.START));
  try {
    const { data } = yield login(userLogin, password, recaptchaValue);

    yield put(setLoginSession(startLoginSession(data.access)));
    yield put(updateAuthTokens(data));
    yield put(statusAction(constants.LOGIN, actionStatus.SUCCESS));
    yield put(setAuthentication(true));
    window.location = '/';
  } catch (err) {
    window.turnstile.reset();

    const message = getMessage(err);
    yield put(
      statusAction(constants.LOGIN, actionStatus.ERROR, {
        message
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.login.${message}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );

    yield put(setAuthentication(false));
  }
}

function* handleExternalLogin({ authorization }) {
  yield put(statusAction(constants.EXTERNAL_LOGIN, actionStatus.START));
  try {
    if (!authorization?.access || !authorization?.refresh) {
      throw new Error('Missing external login tokens');
    }

    const user = getUserInformationFromToken(authorization.access);

    yield put(setAuthentication(false));
    yield localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
    yield localStorage.removeItem('refreshToken');
    yield localStorage.removeItem('accessToken');
    yield put(setLoginSession(startLoginSession(authorization.access)));
    yield localStorage.setItem('accessToken', authorization.access);
    yield localStorage.setItem('refreshToken', authorization.refresh);
    yield put(setUserInformation(user));
    yield put(statusAction(constants.EXTERNAL_LOGIN, actionStatus.SUCCESS));
    yield put(setAuthentication(true));
    window.location = '/dashboard';
  } catch (err) {
    yield localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
    yield localStorage.removeItem('refreshToken');
    yield localStorage.removeItem('accessToken');
    window.turnstile.reset();

    const message = getMessage(err);
    yield put(
      statusAction(constants.EXTERNAL_LOGIN, actionStatus.ERROR, {
        message
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.login.${message}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );

    yield put(setAuthentication(false));
    yield put(push('/login'));
  }
}

function* handleLogout({ url }) {
  yield put(statusAction(constants.LOGOUT, actionStatus.START));
  try {
    yield logout();
    yield put(setAuthentication(false));

    yield localStorage.removeItem('accessToken');
    yield localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY);
    yield localStorage.removeItem('refreshToken');
    yield sessionStorage.setItem('manual_logout', true);

    yield put(statusAction(constants.LOGOUT, actionStatus.SUCCESS));
    if (url) {
      yield put(push(url));
    }
  } catch (err) {
    const error = getError(err);
    yield put(
      statusAction(constants.LOGOUT, actionStatus.ERROR, {
        message: error
      })
    );
  }
}

function* handleCheckAuthentication() {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const isAuthenticated = yield select(getIsAuthenticated);

  if (refreshToken && accessToken) {
    yield put(setLoginSession(restoreLoginSession(accessToken)));
    // always load user information
    const user = getUserInformationFromToken(accessToken);
    yield put(setUserInformation(user));

    if (!isAuthenticated) {
      yield put(setAuthentication(true));
    }
  } else if (isAuthenticated) {
    yield put(setAuthentication(false));
  }
}

function* handleRequestPasswordReset({ resetLogin, recaptchaValue }) {
  yield put(statusAction(constants.REQUEST_PASSWORD_RESET, actionStatus.START));
  try {
    yield requestResetPassword(resetLogin, recaptchaValue);

    yield put(statusAction(constants.REQUEST_PASSWORD_RESET, actionStatus.SUCCESS));
    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.SUCCESS,
        message: 'form.request_success',
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  } catch (err) {
    window.turnstile.reset();

    const error = getError(err);
    yield put(
      statusAction(constants.REQUEST_PASSWORD_RESET, actionStatus.ERROR, {
        message: error
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.auth.${error}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  }
}

function* handleCheckTokenCode({ value }) {
  yield put(statusAction(constants.CHECK_TOKEN_CODE, actionStatus.START));
  try {
    const { data } = yield checkToken(value);
    yield put(statusAction(constants.CHECK_TOKEN_CODE, actionStatus.SUCCESS, { payload: data }));
  } catch (err) {
    const error = getError(err);
    yield put(
      statusAction(constants.CHECK_TOKEN_CODE, actionStatus.ERROR, {
        message: error
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.auth.${error}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  }
}

function* handleResetPasswordNonAuth({ values, auth }) {
  yield put(statusAction(constants.RESET_PASSWORD_NON_AUTH, actionStatus.START));
  try {
    yield resetPasswordNonAuth(values, auth);
    yield put(statusAction(constants.RESET_PASSWORD_NON_AUTH, actionStatus.SUCCESS));

    yield put(push('/login'));
    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.SUCCESS,
        message: 'form.success',
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  } catch (err) {
    const error = getError(err);
    yield put(
      statusAction(constants.RESET_PASSWORD_NON_AUTH, actionStatus.ERROR, {
        message: error
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.user.${error}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  }
}

function* handleRegisterUser({ values, auth, recaptchaValue, email }) {
  yield put(statusAction(constants.REGISTER, actionStatus.START));
  try {
    const { data } = yield resetPasswordNonAuth(values, auth, recaptchaValue);
    yield put(statusAction(constants.REGISTER, actionStatus.SUCCESS));

    // get user information from token
    const user = getUserInformationFromToken(data.access);

    const filteredUser = { ...user };
    delete filteredUser.id;
    const userData = {
      ...filteredUser,
      ...values
    };
    yield registerUserWithDetails(userData, user.id, data.access);

    yield put(statusAction(constants.REGISTER, actionStatus.SUCCESS));

    // if email contains +service@netzsch.com or +manager@netzsch.com, redirect to manager.get-notify.com
    if (email.includes('+service@netzsch.com') || email.includes('+manager@netzsch.com')) {
      // check if current location contains staging or development
      let environment = '';
      if (window.location.hostname.includes('staging')) {
        environment = '-staging';
      } else if (window.location.hostname.includes('development') || window.location.hostname.includes('localhost')) {
        environment = '-development';
      }
      window.location = `https://manager${environment}.get-notify.com`;
      return;
    }

    yield handleLogin({ userLogin: user['e-mail'], password: values.password, recaptchaValue });
    yield put(push('/dashboard'));

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.SUCCESS,
        message: 'form.register_success',
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  } catch (err) {
    window.turnstile.reset();

    const error = getError(err);
    yield put(
      statusAction(constants.REGISTER, actionStatus.ERROR, {
        message: error
      })
    );

    yield put(
      addNotification({
        key: generateId(),
        type: actionStatus.ERROR,
        message: `errors.register.${error}`,
        notificationType: NOTIFICATION_TYPE_INTERACTION
      })
    );
  }
}

export function* watchLoadOrders() {
  yield takeLatest(constants.LOGIN, handleLogin);
  yield takeLatest(constants.EXTERNAL_LOGIN, handleExternalLogin);
  yield takeLatest(constants.LOGOUT, handleLogout);
  yield takeLatest(constants.CHECK_AUTHENTICATION, handleCheckAuthentication);
  yield takeLatest(constants.REQUEST_PASSWORD_RESET, handleRequestPasswordReset);
  yield takeLatest(constants.CHECK_TOKEN_CODE, handleCheckTokenCode);
  yield takeLatest(constants.RESET_PASSWORD_NON_AUTH, handleResetPasswordNonAuth);
  yield takeLatest(constants.REGISTER, handleRegisterUser);
  yield takeLatest(constants.UPDATE_AUTH_TOKENS, handleUpdateTokens);
}
